import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import LoginUserType from "../classes/LoginUserType";
import ActivityTracker from "../models/ActivityTracker";
import PaginationView from "./PaginationView";
import Query from "../classes/Query";
import NOKUser from "../models/NOKUser";
import ActivityTrackers from "../classes/ActivityTrackers";
import DateFormat from "../classes/DateFormat";
import ActivityTrackersRequest from "../models/ActivityTrackersRequest";
import ListWrapper from "../utils/ListWrapper";
import HospitalUtils from "../classes/HospitalUtils";
import Administrator from "../models/Administrator";
import TextView from "./TextView";
import ActivityType from "../classes/ActivityType";
import LabelDropdown from "./LabelDropdown";
import CollectionUtils from "../utils/CollectionUtils";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

export interface ActivityTrackerListProps extends BaseUIProps {
  key?: string;
  user: NOKUser;
  administrator?: Administrator;
}

class _ActivityTrackerListState extends ObservableComponent<ActivityTrackerListProps> {
  static defaultProps = { user: null, administrator: null };
  query: ActivityTrackers = null;
  activityTrackerList: Array<ActivityTracker> = ListWrapper.widget(
    this,
    "activityTrackerList"
  );
  request: ActivityTrackersRequest = null;
  type: ActivityType = ActivityType.Login;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: ActivityTrackerListProps) {
    super(props);

    this.initState();
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public get administrator(): Administrator {
    return this.props.administrator;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.on(
      [
        "request",
        "request.activityType",
        "request.applyActivityType",
        "request.ascending",
        "request.hospital",
        "request.offset",
        "request.orderBy",
        "request.pageSize",
        "request.user",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.on(["query", "query.items"], this.computeActivityTrackerList);

    this.computeActivityTrackerList();

    this.updateSyncProperty("user", this.props.user);

    this.updateSyncProperty("administrator", this.props.administrator);

    this.on(
      [
        "activityTrackerList",
        "activityTrackerList.activityType",
        "activityTrackerList.administrator",
        "activityTrackerList.administrator.name",
        "activityTrackerList.createDate",
        "activityTrackerList.user",
        "activityTrackerList.user.name",
        "activityTrackerList.userType",
        "administrator",
        "query",
        "query.count",
        "query.items",
        "request",
        "request.offset",
        "request.pageSize",
        "type",
        "user",
        "user.userType",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: ActivityTrackerListProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }

    if (prevProps.administrator !== this.props.administrator) {
      this.updateObservable(
        "administrator",
        prevProps.administrator,
        this.props.administrator
      );

      this.fire("administrator", this);
    }
  }
  public setQuery(val: ActivityTrackers): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getActivityTrackers(
          UsageConstants.QUERY_GETACTIVITYTRACKERS_ACTIVITYTRACKERLIST_PROPERTIES_QUERY_COMPUTATION,
          this.request
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setActivityTrackerList(val: Array<ActivityTracker>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.activityTrackerList,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl(
      "activityTrackerList",
      this.activityTrackerList,
      val
    );

    this.activityTrackerList.clear();

    this.activityTrackerList.addAll(val);

    this.fire("activityTrackerList", this);
  }
  public addToActivityTrackerList(
    val: ActivityTracker,
    index: number = -1
  ): void {
    if (index === -1) {
      if (!this.activityTrackerList.contains(val))
        this.activityTrackerList.add(val);
    } else {
      this.activityTrackerList.remove(
        this.activityTrackerList.elementAt(index)
      );

      this.activityTrackerList.add(val);
    }

    this.fire("activityTrackerList", this, val, true);

    this.updateObservable("activityTrackerList", null, val);
  }
  public removeFromActivityTrackerList(val: ActivityTracker): void {
    this.activityTrackerList.remove(val);

    this.fire("activityTrackerList", this, val, false);

    this.removeObservable("activityTrackerList", val);
  }
  public computeActivityTrackerList = (): void => {
    try {
      this.setActivityTrackerList(Array.from(this.query?.items ?? []));
    } catch (exception) {
      console.log(
        " exception in computeActivityTrackerList : " + exception.toString()
      );

      this.setActivityTrackerList([]);
    }
  };
  public setRequest(val: ActivityTrackersRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setType(val: ActivityType): void {
    let isValChanged: boolean = this.type !== val;

    if (!isValChanged) {
      return;
    }

    this.type = val;

    this.fire("type", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      mainAxisAlignment: ui.MainAxisAlignment.start,
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        ui.Column({
          children: [
            ui.Container({
              margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 0.0, 10.0, new Map()),
              child: LabelDropdown<ActivityType>({
                name: "Activity Type",
                value: this.type,
                items: [
                  null,
                  ...HospitalUtils.getCustomActivityType(this.user),
                ],
                width: 300,
                onChanged: (value) => {
                  this.onChangedDropDownHandler(value);
                },
                builder: (context, item) => {
                  return TextView({ data: item === null ? "All" : item.name });
                },
              }),
              key: "0",
              className: "x18 hc h",
            }),
            ui.Column({
              children: [
                ui.Table({
                  defaultColumnWidth: new ui.FlexColumnWidth(1),
                  children: [
                    ui.TableRow({
                      children: [
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Date Time",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "0",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "User Name",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "1",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "User Type",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "2",
                        }),
                        ui.TableCell({
                          verticalAlignment:
                            ui.TableCellVerticalAlignment.middle,
                          child: TextView({
                            data: "Details",
                            style: new ui.TextStyle({
                              fontFamily:
                                cStyle.tTextViewTableHeaderFontFamilyOn,
                              color: cStyle.tTextViewTableHeaderColorOn,
                              fontSize: cStyle.tTextViewTableHeaderFontSizeOn,
                              fontWeight:
                                cStyle.tTextViewTableHeaderFontWeightOn,
                            }),
                            className: "hc",
                          }),
                          className: "hc",
                          key: "3",
                        }),
                      ],
                      className: "x21 hc",
                      key: "0",
                    }),
                    this.query !== null && this.query.items.isNotEmpty
                      ? this.activityTrackerList.expand((forItem) => [
                          ui.TableRow({
                            children: [
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: new DateFormat(
                                    "dd MMM yyyy hh:mm:ss a EEE"
                                  ).format(forItem.createDate),
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "0",
                              }),
                              this.administrator !== null
                                ? ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data:
                                        forItem.administrator !== null
                                          ? forItem.administrator.name
                                          : forItem.user.name,
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                  })
                                : ui.TableCell({
                                    verticalAlignment:
                                      ui.TableCellVerticalAlignment.middle,
                                    child: TextView({
                                      data: forItem.user.name,
                                      softWrap:
                                        cStyle.tTextViewTableCellTextSoftWrapOn,
                                      style: new ui.TextStyle({
                                        fontFamily:
                                          cStyle.tTextViewTableCellTextFontFamilyOn,
                                        color:
                                          cStyle.tTextViewTableCellTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewTableCellTextFontSizeOn,
                                      }),
                                      className: "hc",
                                    }),
                                    className: "hc",
                                  }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.userType.name,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "2",
                              }),
                              ui.TableCell({
                                verticalAlignment:
                                  ui.TableCellVerticalAlignment.middle,
                                child: TextView({
                                  data: forItem.activityType.name,
                                  softWrap:
                                    cStyle.tTextViewTableCellTextSoftWrapOn,
                                  style: new ui.TextStyle({
                                    fontFamily:
                                      cStyle.tTextViewTableCellTextFontFamilyOn,
                                    color: cStyle.tTextViewTableCellTextColorOn,
                                    fontSize:
                                      cStyle.tTextViewTableCellTextFontSizeOn,
                                  }),
                                  className: "hc",
                                }),
                                className: "hc",
                                key: "3",
                              }),
                            ],
                            className: "x8d hc",
                            key: forItem?.ident,
                          }),
                        ])
                      : [],
                  ],
                  className: "xef hc h",
                  key: "0",
                }),
                this.query !== null && this.query.items.isEmpty
                  ? ui.Column({
                      mainAxisAlignment: ui.MainAxisAlignment.center,
                      children: [
                        ui.AssetImage({
                          path: "images/emptyList.png",
                          width: 300,
                          height: 300,
                          className: "x32 hc vc",
                          key: "0",
                        }),
                        TextView({
                          data: "No Records",
                          style: new ui.TextStyle({
                            fontWeight: ui.FontWeight.bold,
                            fontSize: 16,
                          }),
                          className: "x4f hc",
                          key: "1",
                        }),
                      ],
                      className: "x1a hc",
                    })
                  : [],
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.end,
                  children: [
                    PaginationView({
                      pageSize: this.request.pageSize,
                      total: this.query !== null ? this.query.count : 0,
                      offset: this.request.offset,
                      onChange: (pageSize, offset) => {
                        this.onPaginationViewChanged(pageSize, offset);
                      },
                      states: ui.joinStates(
                        {
                          "data-visibility":
                            this.query !== null && this.query.items.isNotEmpty,
                        },
                        {}
                      ),
                      key: "0",
                    }),
                  ],
                  className: "xcfd hc h",
                  key: "2",
                }),
              ],
              className: "x58 hc h",
              key: "1",
            }),
          ],
          className: "x06 hc h",
          key: "0",
        }),
      ],
      className: ui.join(
        this.props.className,
        "ActivityTrackerList xa9 hc vc h v"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setType(null);

    if (this.administrator !== null) {
      this.setRequest(
        new ActivityTrackersRequest({
          pageSize: 50,
          offset: 0,
          orderBy: "",
          ascending: true,
          applyActivityType: false,
        })
      );
    } else if (this.user !== null) {
      this.setRequest(
        new ActivityTrackersRequest({
          pageSize: 50,
          offset: 0,
          orderBy: "",
          ascending: true,
          applyActivityType: false,
          user: this.user,
          hospital: this.user.hospital,
        })
      );
    }
  };
  public onPaginationViewChanged = (pageSize: number, offset: number): void => {
    this.request.setPageSize(pageSize);

    this.request.setOffset(offset);

    this.setRequest(this.request.deepClone());
  };
  public onChangedDropDownHandler = (value: ActivityType): void => {
    this.setType(value);

    if (this.type !== null) {
      this.request.setApplyActivityType(true);

      this.request.setActivityType(this.type);
    } else {
      this.request.setApplyActivityType(false);
    }

    this.setRequest(this.request.deepClone());
  };
}
export default function ActivityTrackerList(props: ActivityTrackerListProps) {
  return React.createElement(_ActivityTrackerListState, {
    ..._ActivityTrackerListState.defaultProps,
    ...props,
  });
}
